import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  inject,
  input,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

import { ReportFmtPipe, ReportTPipe } from '../report-i18n.pipe';
import { ReportI18nService } from '../report-i18n.service';
import { ReportRevealDirective } from '../report-reveal.directive';
import { CrossSubjectView, ReportLevel, SubjectRow } from '../report.model';

/** Variante de pastille par palier — même appariement que la carte « ce que cela signifie ». */
const BADGE_VARIANT: Record<ReportLevel, string> = {
  weak: 'danger',
  average: 'warning',
  good: 'info',
  excellent: 'success',
};

/** Une ligne du tableau, prête à afficher. */
interface SubjectRowView {
  subjectId: number;
  subjectName: string;
  hasDiagnostic: boolean;
  highlighted: boolean;
  percentage: number;
  pointsVars: Record<string, unknown>;
  meterClass: string;
  badgeClass: string;
  levelLabel: string;
}

/** Le pied de tableau : la moyenne, calculée sur les seules matières passées. */
interface OverallView {
  percentage: number;
  pointsVars: Record<string, unknown>;
  meterClass: string;
  badgeClass: string;
  levelLabel: string;
  delay: number;
}

/**
 * Bloc « toutes les matières » — l'un des deux seuls du rapport à dépasser la
 * matière.
 *
 * Une matière affectée mais non diagnostiquée figure quand même : c'est ce
 * qu'il reste à faire, et l'omettre le tairait. Elle n'affiche en revanche ni
 * note, ni pourcentage, ni palier — un test qui n'a pas eu lieu n'est pas un
 * zéro, et une jauge à 0 % se lirait comme un résultat catastrophique.
 */
@Component({
  selector: 's2e-report-all-subjects',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReportFmtPipe, ReportTPipe, ReportRevealDirective, TranslateModule],
  templateUrl: './all-subjects.section.html',
  host: { '[style.display]': "'contents'" },
})
export class AllSubjectsSection {
  private readonly i18n = inject(ReportI18nService);

  readonly cross = input.required<CrossSubjectView>();

  protected readonly rows = computed<SubjectRowView[]>(() =>
    this.cross().subjectRows.map((row) => ({
      subjectId: row.subjectId,
      subjectName: row.subjectName,
      hasDiagnostic: row.hasDiagnostic,
      highlighted: row.highlighted,
      percentage: row.percentage,
      pointsVars: { score: { mark: row.score }, max: { n: row.maxScore } },
      meterClass: this.meterClass(row.level),
      badgeClass: this.badgeClass(row.level),
      levelLabel: this.levelLabel(row.band?.label ?? null, row.level),
    })),
  );

  /**
   * Moyenne du tableau.
   *
   * Le modèle ne porte que la moyenne des **pourcentages** ; la note moyenne
   * affichée en pied est la même moyenne appliquée aux notes, et non un
   * chiffre venu d'ailleurs. Les matières non diagnostiquées en sont exclues :
   * elles ne pèsent sur aucun total, sans quoi une matière non passée tirerait
   * la moyenne vers le bas.
   *
   * Le palier de la moyenne se retrouve dans les bornes déjà portées par les
   * lignes : le catalogue des paliers est commun à toutes les matières, il n'y
   * a donc aucun seuil à recopier ici.
   */
  protected readonly overall = computed<OverallView | null>(() => {
    const all = this.cross().subjectRows;
    const taken = all.filter((row) => row.hasDiagnostic);
    if (taken.length === 0) return null;

    const percentage = this.cross().overallScorePercentage;
    const mark = taken.reduce((sum, row) => sum + row.score, 0) / taken.length;
    const max = taken.reduce((sum, row) => sum + row.maxScore, 0) / taken.length;
    const band = this.bandFor(percentage, taken);

    return {
      percentage,
      pointsVars: { score: { mark }, max: { n: max } },
      meterClass: this.meterClass(band?.level ?? null),
      badgeClass: this.badgeClass(band?.level ?? null),
      levelLabel: this.levelLabel(band?.label ?? null, band?.level ?? null),
      /* Le pied prolonge la cascade des lignes : il s'allume après la dernière. */
      delay: all.length * 80,
    };
  });

  private bandFor(percentage: number, rows: readonly SubjectRow[]) {
    for (const row of rows) {
      const band = row.band;
      if (band && percentage >= band.minPercentage && percentage <= band.maxPercentage) {
        return band;
      }
    }
    return null;
  }

  private meterClass(level: ReportLevel | null): string {
    return level ? `meter meter--${level}` : 'meter';
  }

  private badgeClass(level: ReportLevel | null): string {
    return `badge badge--${level ? BADGE_VARIANT[level] : 'neutral'} badge--md`;
  }

  /** Intitulé du palier : celui de l'API d'abord, les clés `levels.*` en repli. */
  private levelLabel(bandLabel: string | null, level: ReportLevel | null): string {
    if (bandLabel) return bandLabel;
    return level ? this.i18n.t(`levels.${level}`) : '';
  }
}
